/* eslint-disable react/button-has-type */
/* eslint-disable react/react-in-jsx-scope */
import { useMemo, useState, useEffect, useRef } from 'react';
import { MapboxOverlay, MapboxOverlayProps } from '@deck.gl/mapbox/typed';
import Map, { useControl, AttributionControl, MapRef } from 'react-map-gl/maplibre';

import SourceToolbar from './SourceToolbar';
import MapToolbar from './MapToolbar';
import {
  getCurrentLocationChildSensors,
  getCurrentLocationFloorplan,
  getCurrentLocation,
  getHighlightedItem,
  getActiveMarker,
  getMotionThreshold,
  getUserPosition,
  getThemeMode,
  getCurrentLocationChildLocations,
  getUserLocation,
  getBleLocSwitchStatus,
  getLocationsById,
  getLocSensorStatusData,
} from '../../state/selectors';
import { VarName } from '../../utils/varNames';
import {
  MarkerData,
  MapCentrePosition,
  calculateViewSize,
  getMapLibreStyle,
  MAP_DEFAULT_PITCH,
  HoveredLoc,
} from './mapHelpers';
import { SensorValueItem } from '../CalendarView/helpers';
import CustomMarker from './MapLayers/CustomMarker';
import useStyles from '../../styles';
import { getMotionOccupancyValue } from '../../utils/motionEvents';
import { StorageTypes, persistState, retrieveState } from '../../utils/persistentState';
import { DeckGLViewPort } from './types';
import CustomLocationMarker from './MapLayers/CustomLocationMarker';
import { useAppDispatch, useAppSelector } from '../../state/store';
import useFloorLayers from './MapLayers/useFloorLayers';
import get3DBuildingLayer from './MapLayers/3dBuildingLayer';

function DeckGLOverlay(
  props: MapboxOverlayProps & {
    // eslint-disable-next-line react/no-unused-prop-types, react/require-default-props
    interleaved?: boolean;
  }
) {
  const overlay = useControl<MapboxOverlay>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

interface MapParams {
  overrideData?: SensorValueItem[]; // e.g. on calendar page to use summary data instead of live data
  showSourceToolbar?: boolean;
  mapCentrePosition?: MapCentrePosition;
  interactive?: boolean;
}

function MapGL({
  overrideData,
  showSourceToolbar,
  mapCentrePosition,
  interactive,
}: MapParams): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const mapRef = useRef<MapRef>(null);

  // Selectors
  const themeMode = useAppSelector(getThemeMode);
  const floorPlan = useAppSelector(getCurrentLocationFloorplan);
  const childLocations = useAppSelector(getCurrentLocationChildLocations);
  const childSensors = useAppSelector(getCurrentLocationChildSensors);
  const currentLocation = useAppSelector(getCurrentLocation);
  const highlightedItem = useAppSelector(getHighlightedItem);
  const activeMarker = useAppSelector(getActiveMarker);
  const motionThreshold = useAppSelector(getMotionThreshold);
  const userPosition = useAppSelector(getUserPosition);
  const userLocation = useAppSelector(getUserLocation);
  const allowBleLocSwitch = useAppSelector(getBleLocSwitchStatus);
  const locSensorStatusData = useAppSelector(getLocSensorStatusData);
  const locationsById = useAppSelector(getLocationsById);

  // Derived values
  const show3DBuilding =
    floorPlan &&
    floorPlan.features.filter((feature) => feature.properties.type === 'area.floor').length > 1;

  const mapLibreStyle = getMapLibreStyle(themeMode);
  // Which data should we show on the map (activeMarker is selected data but can be overriden
  // by highlightedItem when hovering on data of another type
  const activeVarName = highlightedItem?.varName ?? activeMarker;
  const showCustomMarker = activeVarName !== VarName.MotionEvent;

  const defaultViewState = useMemo(
    () => ({
      latitude: userPosition?.lat ?? 53,
      longitude: userPosition?.lng ?? -1,
      zoom: userPosition?.lat && userPosition?.lng ? 21 : 4,
      pitch: MAP_DEFAULT_PITCH,
      bearing: 0,
    }),
    [userPosition]
  );

  // State
  const [hoveredLoc, setHoveredLoc] = useState<HoveredLoc>();
  const [performAutoZoom, setPerformAutoZoom] = useState(false);
  const [viewState, setViewState] = useState<DeckGLViewPort>(
    (retrieveState(StorageTypes.MapLocation) as DeckGLViewPort) ?? defaultViewState
  );

  // Extract and shape the sensor information needed to create the map
  // filter for only those needed and change data where overriden or needed (e.g. motion)
  const markerData = useMemo(() => {
    if (!childSensors) return [];
    const dataItems: MarkerData[] = [];
    childSensors?.forEach((sensor) => {
      const { id, data, name, shortName, position } = sensor;
      // Find the object within the `data` array
      const dataObject = data?.find((d) => d.varName === activeVarName);
      // Remove sensors that don't have data for the activeVarName
      let isValidSensor: boolean | undefined = dataObject !== undefined;

      const onlineStatusValue = data?.find((d) => d.varName === VarName.OnlineStatus)?.value;
      // Remove retired sensors if not showing onlineStatus data
      if (isValidSensor && activeVarName !== VarName.OnlineStatus) {
        if (onlineStatusValue === -2) isValidSensor = false;
      }

      let activeVarNameValue = dataObject?.value;
      // Change data value for overriden data
      if (overrideData) {
        const newValue = overrideData.find((d) => d.id === id)?.value;
        // Update the data value if necessary
        if (newValue !== null) activeVarNameValue = newValue;
      } else if (data && activeVarName === VarName.MotionEvent) {
        // Transform motion data to a form we can use
        const motionData = data.find((d) => d.varName === VarName.MotionEvent);
        if (motionData) {
          const { time, value } = motionData;
          const occValue = getMotionOccupancyValue(motionThreshold, time, value);
          motionData.value = occValue;
          activeVarNameValue = occValue;
        }
      }

      if (isValidSensor && activeVarNameValue !== undefined && position && dataObject?.time) {
        dataItems.push({
          id,
          name: name ?? 'UNKNOWN',
          shortName: shortName ?? 'UNKNOWN',
          position,
          value: activeVarNameValue,
          time: dataObject.time,
          varName: activeVarName,
          isOnline: onlineStatusValue !== undefined && onlineStatusValue > 0,
          detail: dataObject?.detail ?? null,
        });
      }
    });
    return dataItems;
  }, [childSensors, activeVarName, overrideData, motionThreshold]);

  const handleViewState = (viewParam: DeckGLViewPort) => {
    setViewState(viewParam);
    persistState(viewParam, StorageTypes.MapLocation);
  };

  const fitToView = (forceUserPos = false) => {
    // Size needed to fit all floorplan and sensors
    const viewSizeState = calculateViewSize(
      childLocations,
      childSensors,
      currentLocation,
      floorPlan,
      viewState
    );
    if (!viewSizeState) return;

    // display floorplan to slightly right side when floating contents are visible in left side
    const spacingParam = {
      padding: {
        left: mapCentrePosition === MapCentrePosition.right ? 600 : 0,
        top: mapCentrePosition === MapCentrePosition.bottom ? 300 : 0,
      },
    };

    const userAutoPosition =
      (forceUserPos || allowBleLocSwitch) && !!userPosition && userLocation === currentLocation;

    if (userAutoPosition && userPosition.lat && userPosition.lng) {
      const userZoomParam = {
        latitude: userPosition.lat,
        longitude: userPosition.lng,
        zoom: Math.max(viewSizeState.zoom, 19),
      };
      handleViewState({ ...viewSizeState, ...spacingParam, ...userZoomParam });
    } else {
      handleViewState({ ...viewSizeState, ...spacingParam });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fitToView(), [floorPlan]); // Auto-zoom when floorplan downloaded

  useEffect(() => setPerformAutoZoom(true), [currentLocation]); // Track location changes
  useEffect(() => {
    // Auto-zoom when location data downloaded (after location change)
    if (performAutoZoom && childLocations) {
      setPerformAutoZoom(false);
      fitToView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childLocations]);

  const resetOrientation = () => {
    const newViewState = { ...viewState, pitch: MAP_DEFAULT_PITCH, bearing: 0 };
    handleViewState({ ...newViewState });
  };

  // create separate layer as dependencies list were getting larger with combined condition
  const buildingLayers = useMemo(() => {
    let layer = null;
    if (show3DBuilding && floorPlan) {
      const buildingLayer = get3DBuildingLayer(
        currentLocation,
        childLocations,
        activeMarker,
        locSensorStatusData,
        floorPlan,
        motionThreshold,
        locationsById,
        hoveredLoc,
        setHoveredLoc,
        dispatch
      );

      layer = [buildingLayer];
    }
    return layer;
  }, [
    activeMarker,
    childLocations,
    currentLocation,
    dispatch,
    floorPlan,
    hoveredLoc,
    locSensorStatusData,
    locationsById,
    motionThreshold,
    show3DBuilding,
  ]);

  const floorLayers = useFloorLayers(viewState, markerData, overrideData);

  return (
    <div className={classes.mapContainer}>
      <div style={{ height: '100%', width: '100%', position: 'absolute' }}>
        <Map
          interactive={interactive}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...viewState}
          ref={mapRef}
          onMove={(evt) => handleViewState(evt.viewState)}
          mapStyle={mapLibreStyle}
          attributionControl={false}
        >
          <DeckGLOverlay
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            layers={show3DBuilding ? buildingLayers : floorLayers}
            interleaved
            style={{ height: '100%' }}
          />
          {showCustomMarker && markerData && <CustomMarker markerData={markerData} />}
          {/* show floor levels summary ring in desktop view */}
          {childLocations.length > 0 && (
            <CustomLocationMarker hoveredLoc={hoveredLoc} setHoveredLoc={setHoveredLoc} />
          )}
          <AttributionControl customAttribution="&copy; OpenStreetMap" compact={false} />
          <MapToolbar
            fitToView={fitToView}
            resetOrientation={resetOrientation}
            pitch={viewState.pitch}
            bearing={viewState.bearing}
          />
          {showSourceToolbar && <SourceToolbar />}
        </Map>
      </div>
    </div>
  );
}

MapGL.defaultProps = {
  overrideData: undefined,
  showSourceToolbar: true,
  mapCentrePosition: MapCentrePosition.default,
  interactive: true,
};

export default MapGL;
